import { useQuery } from "@tanstack/react-query"
import { CircleAlert, FileText, Lock } from "lucide-react"
import { useParams } from "react-router"

import { api } from "@/api/cliente"
import { monto } from "@/componentes/tesoreria/dinero"
import { Insignia } from "@/componentes/ui/Insignia"
import { comoFecha, comoFechaHora } from "@/lib/fechas"

type IngresoPublico = {
  codigo: string
  por_determinar: boolean
  monto: string
  moneda: string
  banco: string
  fecha_valor: string
  referencia: string | null
  concepto: string
  pagador: string | null
  pagador_tipo: string | null
  en_su_cuenta: { rotulo: string; monto: string }[]
  reportado_por: string | null
  reportado_en: string | null
  soportes: { numero: number; nombre: string; mime: string }[]
}

/**
 * Un ingreso visto desde «Ver ingreso» del bot. **Sin sesión, sin menú y sin poder
 * tocar nada**, como `OrdenPublica`: vive fuera del `AppShell` y lo que se ve lo decide
 * la lista blanca del servidor (`IngresoPublico`).
 *
 * ⚠ **Dice el estado de HOY**: el enlace de un aviso «por determinar» enseña el pagador
 * en cuanto se identifica, y sigue siendo el MISMO ingreso.
 *
 * ⚠ **El error es uno solo**: caducado, revocado, ingreso anulado o inventado.
 */
export function IngresoPublica() {
  const { token = "" } = useParams()
  const consulta = useQuery({
    queryKey: ["publico", "ingreso", token],
    queryFn: () => api.get<IngresoPublico>(`/api/v1/publico/ingresos/${token}`),
    retry: false,
  })

  if (consulta.isPending) {
    return (
      <p role="status" className="p-10 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
        Cargando…
      </p>
    )
  }
  if (consulta.isError || !consulta.data) {
    return (
      <div className="mx-auto max-w-lg p-10 text-center">
        <CircleAlert size={28} aria-hidden={true} style={{ color: "var(--fg-muted)" }} />
        <h1 className="mt-3 text-lg font-semibold">Este enlace ya no vale</h1>
        <p className="mt-2 text-sm" style={{ color: "var(--fg-muted)" }}>
          Puede haber caducado, haberse revocado o el ingreso haberse anulado. Pídele uno nuevo a
          quien te lo mandó.
        </p>
      </div>
    )
  }

  const i = consulta.data
  return (
    <div className="mx-auto max-w-2xl p-4 sm:p-8">
      <p
        className="mb-4 flex items-center gap-2 rounded-[var(--radius-control)] border border-dashed px-3 py-2 text-xs"
        style={{ borderColor: "var(--border)", color: "var(--fg-muted)" }}
      >
        <Lock size={12} aria-hidden={true} />
        Vista de sólo lectura. El enlace caduca a los siete días.
      </p>

      <p className="text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
        Ingreso {i.codigo}
      </p>
      <h1 className="mt-1 text-xl font-semibold tabular-nums">
        {monto(i.monto)} {i.moneda} en {i.banco}
      </h1>
      <p className="mt-1 text-sm">{i.concepto}</p>
      <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
        <Insignia tono={i.por_determinar ? "aviso" : "exito"}>
          {i.por_determinar ? "Pagador por determinar" : "Pagador identificado"}
        </Insignia>
      </div>

      <section className="tarjeta mt-6">
        <h2 className="text-sm font-medium">Detalle</h2>
        <dl className="tabular-nums mt-2 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
          <dt style={{ color: "var(--fg-muted)" }}>Fecha valor</dt>
          <dd>{comoFecha(i.fecha_valor)}</dd>
          {i.referencia && (
            <>
              <dt style={{ color: "var(--fg-muted)" }}>Referencia</dt>
              <dd>{i.referencia}</dd>
            </>
          )}
          <dt style={{ color: "var(--fg-muted)" }}>Pagador</dt>
          <dd>
            {i.pagador
              ? `${i.pagador}${i.pagador_tipo ? ` (${i.pagador_tipo.toLowerCase()})` : ""}`
              : "Por determinar"}
          </dd>
          {i.en_su_cuenta.map((l) => (
            <div key={l.rotulo} className="contents">
              <dt style={{ color: "var(--fg-muted)" }}>{l.rotulo}</dt>
              <dd>
                {monto(l.monto)} {i.moneda}
              </dd>
            </div>
          ))}
          {i.reportado_por && (
            <>
              <dt style={{ color: "var(--fg-muted)" }}>Reportó</dt>
              <dd>
                {i.reportado_por}
                {i.reportado_en ? ` · ${comoFechaHora(i.reportado_en)}` : ""}
              </dd>
            </>
          )}
        </dl>
      </section>

      <section className="tarjeta mt-6">
        <h2 className="flex items-center gap-2 text-sm font-medium">
          <FileText size={14} aria-hidden={true} />
          Comprobante
        </h2>
        {i.soportes.length === 0 ? (
          <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
            No hay archivos adjuntos.
          </p>
        ) : (
          <ul className="mt-2 space-y-1 text-sm">
            {i.soportes.map((s) => (
              <li key={s.numero}>
                {/* La MISMA ruta pública, con el MISMO token: sin un id que cambiar. */}
                <a
                  href={`/api/v1/publico/ingresos/${token}/soportes/${s.numero}`}
                  target="_blank"
                  rel="noreferrer"
                  className="underline"
                >
                  {s.nombre}
                </a>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
